/**
 * Template Gallery Component
 * Displays templates in a grid with filtering and search
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TemplateCard from './TemplateCard';
import type { FlowTemplate } from '../../types/flow';
import { useAdmin } from '../../hooks/useAdmin';

interface TemplateGalleryProps {
  templates: FlowTemplate[];
  loading?: boolean;
  onLoadTemplate: (template: FlowTemplate) => void;
  onRunTemplate?: (template: FlowTemplate) => void;
  onDeleteTemplate?: (template: FlowTemplate) => void;
  emptyMessage?: string;
}

const TemplateGallery: React.FC<TemplateGalleryProps> = ({
  templates,
  loading = false,
  onLoadTemplate,
  onRunTemplate,
  onDeleteTemplate,
  emptyMessage = 'No templates available'
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const { isAdmin } = useAdmin();

  // Filter templates based on search and category
  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      // Category filter
      if (selectedCategory !== 'All' && template.category !== selectedCategory) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = template.metadata.name.toLowerCase().includes(query);
        const matchesDescription = template.metadata.description?.toLowerCase().includes(query);
        const matchesTags = template.metadata.tags?.some(tag => 
          tag.toLowerCase().includes(query)
        );
        
        if (!matchesName && !matchesDescription && !matchesTags) {
          return false;
        }
      }

      return true;
    });
  }, [templates, searchQuery, selectedCategory]);

  // Get unique categories from templates
  const availableCategories = useMemo(() => {
    const categories = new Set(
      templates
        .map(t => t.category)
        .filter((c): c is string => Boolean(c))
    );
    return ['All', ...Array.from(categories).sort()];
  }, [templates]);

  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        p={4} 
        gap={2}
      >
        <CircularProgress size={32} sx={{ color: 'var(--color-accent-primary)' }} />
        <Typography sx={{ color: 'var(--color-text-secondary)' }}>
          Loading templates...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Box 
        sx={{ 
          display: 'flex', 
          gap: 2, 
          marginBottom: 2,
          flexWrap: 'wrap'
        }}
      >
        {/* Search */}
        <TextField
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="outlined"
          size="small"
          sx={{ flexGrow: 1, minWidth: '200px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'var(--color-text-secondary)' }} />
              </InputAdornment>
            ),
            sx: {
              color: 'var(--color-text-primary)',
              backgroundColor: 'var(--node-header-background)',
              borderColor: 'var(--node-border-color)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--node-border-color)'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--color-text-secondary)'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--color-accent-primary)'
              }
            }
          }}
        />

        {/* Category Filter */}
        <FormControl size="small" sx={{ minWidth: '150px' }}>
          <InputLabel 
            id="category-filter-label"
            sx={{ color: 'var(--color-text-secondary)' }}
          >
            Category
          </InputLabel>
          <Select
            labelId="category-filter-label"
            value={selectedCategory}
            label="Category"
            onChange={(e) => setSelectedCategory(e.target.value)}
            sx={{
              color: 'var(--color-text-primary)',
              backgroundColor: 'var(--node-header-background)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--node-border-color)'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--color-text-secondary)'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--color-accent-primary)'
              },
              '& .MuiSvgIcon-root': {
                color: 'var(--color-text-secondary)'
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: 'var(--node-body-background)',
                  border: '1px solid var(--node-border-color)'
                }
              }
            }}
          >
            {availableCategories.map((category) => (
              <MenuItem 
                key={category} 
                value={category}
                sx={{
                  color: 'var(--color-text-primary)',
                  '&:hover': {
                    backgroundColor: 'var(--color-bg-hover)'
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'var(--color-accent-primary)',
                    '&:hover': {
                      backgroundColor: 'var(--color-accent-primary)'
                    }
                  }
                }}
              >
                {category}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Results count */}
      <Typography 
        variant="body2" 
        sx={{ 
          color: 'var(--color-text-secondary)', 
          marginBottom: 2 
        }}
      >
        {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} found
      </Typography>

      {/* Template Grid */}
      {filteredTemplates.length === 0 ? (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          p={4}
        >
          <Typography sx={{ color: 'var(--color-text-secondary)' }}>
            {searchQuery || selectedCategory !== 'All' 
              ? 'No templates match your filters' 
              : emptyMessage
            }
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: 2
          }}
        >
          {filteredTemplates.map((template) => (
            <TemplateCard
              key={template.metadata.id}
              template={template}
              onLoad={onLoadTemplate}
              onRun={onRunTemplate}
              onDelete={onDeleteTemplate}
              showDeleteButton={isAdmin}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TemplateGallery;
